import React, { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import {
  detectPlatformFromUrl,
  resolveSourcePlatform,
} from 'src/utils/insightPlatform'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { ReactComponent as DiscussionIcon } from 'src/assets/svg/discussion-bubbles.svg'
import { ReactComponent as MegaphoneIcon } from 'src/assets/svg/insight-megaphone.svg'
import { ReactComponent as StarRatingIcon } from 'src/assets/svg/insight-star.svg'
import { ReactComponent as PlanetIcon } from 'src/assets/svg/planet.svg'
import { ReactComponent as StrengthIcon } from 'src/assets/svg/smart-insights-trophy.svg'
import { ReactComponent as ChallengeIcon } from 'src/assets/svg/smart-insights-puzzle.svg'
import { getAiToolIcon } from 'src/components/AiToolIcon'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { resolveExternalUrl } from 'src/utils/resolveExternalUrl'
import { RichText } from './RichText'
import styles from './insightDetailModal.module.scss'

export interface InsightSourceLink {
  // Comment insights only: who wrote the cited comment, stored by the
  // generator (a comment has no url of its own, so it cannot be resolved on
  // read). Shown in the same place as customerName.
  author?: string
  // Inbox insights only: the customer in the cited conversation, resolved
  // backend-side by the same rule the chat list uses. Absent when no
  // conversation matches the link.
  customerName?: string
  date: string
  language: string
  text?: string
  url: string
}

export interface InsightSource {
  links?: InsightSourceLink[]
  platform: string
  text: string
  // AI Listening sources are flat: a verbatim quote plus the cited URL, with
  // no `links` array. Other modules leave this undefined.
  url?: string
}

// Icon shown next to a metric stat. Values map to specific SVGs in the modal.
export type InsightMetricIcon = 'bubbles' | 'link' | 'megaphone' | 'star'

// A single line inside a metric card, e.g. "187 mentions found" (emphasis =
// "187", label = "mentions found") or "21% of campaign mentions".
// Omitting `label` renders the number alone, large and accented.
export interface InsightMetricStat {
  emphasis: string
  icon: InsightMetricIcon
  label?: string
  // Number-only stats are accent purple by default; 'dark' renders them in the
  // body colour instead.
  tone?: 'accent' | 'dark'
}

// One card in the metrics row (e.g. "Mention Links" or "Total Discussion").
// A card can hold multiple stats (Home/Review show two side by side) and an
// optional share donut, shown after a vertical divider.
export interface InsightMetricCard {
  chartPercent?: number
  stats: InsightMetricStat[]
  title: string
}

// likelyDriver, observation, suggestedExecution and whyItMatters come from the
// AI Listening backend only. Every other module leaves them undefined and keeps
// the plain `details` rendering below.
export interface InsightDetailData {
  aiRecommendation?: string
  details: string
  // Per-module metrics row (Mention/Post/Chat/Review Links + the totals card).
  // When provided, replaces the default single Mention Links block.
  // Period-level card (inbox period-over-period / overview): its numbers come
  // from every conversation in the window, so the cited sources are only
  // examples — the sources block is hidden rather than shown as evidence.
  isAggregate?: boolean
  // Optional (HomePage Smart Insights): drives the accent colour of the AI
  // Recommendation block (green for strengths, orange for challenges) and the
  // header icon. Omitted elsewhere → neutral styling.
  isStrength?: boolean
  likelyDriver?: string
  metrics?: InsightMetricCard[]
  observation?: string
  // The window the insight covers, resolved by the backend (stored period →
  // source link dates → checkedAt → createdAt). Preferred over deriving it
  // from `sources` here, which is wrong for review-analysis (ignores the
  // stored period) and empty for ai-listening (no source links).
  period?: { from: string; to: string } | null
  // When the citations were produced (AI Listening check date). Flat sources
  // carry no date of their own, so all rows share the run's date.
  sourceDate?: string
  sources: InsightSource[]
  suggestedExecution?: string
  title: string
  whyItMatters?: string
}

interface InsightDetailModalProps {
  data: InsightDetailData
  onClose: () => void
}

// "https://www.silknet.ge/en/foo" -> "silknet.ge"
const citedDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '')
  } catch {
    return url
  }
}

const formatPlatformName = (platform: string): string => {
  if (!platform) return ''
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase()
}

// The stored `link.language` is usually an ISO 639-1 code ("en"). Figma shows
// the full language name with only the first letter capitalized ("English"),
// so map known codes to names and gracefully capitalise anything else.
const LANGUAGE_NAMES: Record<string, string> = {
  ar: 'Arabic',
  az: 'Azerbaijani',
  cs: 'Czech',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  fa: 'Persian',
  fr: 'French',
  he: 'Hebrew',
  hy: 'Armenian',
  it: 'Italian',
  ja: 'Japanese',
  ka: 'Georgian',
  ko: 'Korean',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Portuguese',
  ru: 'Russian',
  sv: 'Swedish',
  tr: 'Turkish',
  uk: 'Ukrainian',
  zh: 'Chinese',
}

const formatLanguage = (lang: string): string => {
  const key = lang.trim().toLowerCase()
  return LANGUAGE_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

// AI Listening sources name the assistant that produced the answer, so the row
// shows that tool's mark instead of a social-platform label.
// A flat source (no `links`, just a quote) whose platform is an AI tool is an
// AI Listening citation — that's what switches the section to "Citation
// Sources" and the per-row "Cited from:" treatment.
const isAiToolSource = (source: InsightSource): boolean =>
  !source.links?.length &&
  Boolean(source.text) &&
  Boolean(getAiToolIcon((source.platform || '').trim()))

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// The backend sends period bounds as UTC instants, and the end bound is the
// last millisecond of its day (…-07-31T23:59:59.999Z). Rendering that in local
// time pushes it into the next day for any positive offset — a July period
// would read "Jul 1 - Aug 1" in Tbilisi — so pin the period line to UTC.
const formatPeriodDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  })

// Renders the correct 20×20 icon for a metric stat.
const MetricStatIcon: FC<{ icon: InsightMetricIcon }> = ({ icon }) => {
  if (icon === 'megaphone') return <MegaphoneIcon className={styles.linkIcon} />
  if (icon === 'star') return <StarRatingIcon className={styles.linkIcon} />
  if (icon === 'bubbles') return <DiscussionIcon className={styles.linkIcon} />
  return (
    <svg
      className={styles.linkIcon}
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.17 10.83c.27.38.62.69 1.02.92.4.23.84.38 1.3.43.45.05.92.01 1.35-.13.44-.14.85-.36 1.19-.67l2.33-2.33a4.01 4.01 0 00-.05-5.72 4.01 4.01 0 00-5.76-.08L9.24 4.52"
        stroke="#8080F7"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M10.83 9.17a3.5 3.5 0 00-1.02-.92 3.5 3.5 0 00-1.3-.43 3.5 3.5 0 00-1.35.13c-.44.14-.85.36-1.19.67L3.64 10.95a4.01 4.01 0 00.05 5.72 4.01 4.01 0 005.76.08l1.31-1.32"
        stroke="#8080F7"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

// 48×48 share donut with the percentage in the middle. r + strokeWidth/2 = 24
// so the ring sits flush inside the 48px box.
const DONUT_RADIUS = 21
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS

const MetricDonut: FC<{ percent: number }> = ({ percent }) => {
  const clamped = Math.max(0, Math.min(100, percent))
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE
  return (
    <span className={styles.metricDonut}>
      <svg
        height="48"
        viewBox="0 0 48 48"
        width="48"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="24"
          cy="24"
          fill="none"
          r={DONUT_RADIUS}
          stroke="#c7ced1"
          strokeWidth="6"
        />
        <circle
          cx="24"
          cy="24"
          fill="none"
          r={DONUT_RADIUS}
          stroke="#8080ff"
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          strokeLinecap="round"
          strokeWidth="6"
          transform="rotate(-90 24 24)"
        />
      </svg>
      <span className={styles.metricDonutLabel}>{Math.round(clamped)}%</span>
    </span>
  )
}

export const InsightDetailModal: FC<InsightDetailModalProps> = ({
  data,
  onClose,
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all')
  const [isScrollable, setIsScrollable] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  useDisableScroll(true)

  useEffect(() => {
    return () => {
      document.body.style.overflowY = ''
    }
  }, [])

  useEffect(() => {
    const el = bodyRef.current
    if (el) {
      setIsScrollable(el.scrollHeight > el.clientHeight)
    }
  })

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const platforms = Array.from(
    new Set(
      data.sources.map((s) => formatPlatformName(resolveSourcePlatform(s)))
    )
  )

  // AI Listening insights ship a structured breakdown; every other module only
  // has the legacy `details` string and must keep rendering exactly as before.
  const hasBreakdown = Boolean(
    data.observation || data.whyItMatters || data.likelyDriver
  )

  const filteredSources = data.sources.filter((source) =>
    selectedPlatform === 'all'
      ? true
      : resolveSourcePlatform(source).toLowerCase() === selectedPlatform
  )

  // Prefer the backend-resolved period. Fall back to deriving it from source
  // link dates for callers that don't pass one (mock data, older payloads);
  // those are bare local-ish date strings, so they keep the local formatter.
  const backendPeriod = data.period?.from ? data.period : null
  const linkDates = backendPeriod
    ? []
    : data.sources
        .flatMap((s) => s.links?.map((l) => l.date) ?? [])
        .filter(Boolean)
        .sort()

  const format = backendPeriod ? formatPeriodDate : formatDate
  const periodStart = backendPeriod ? backendPeriod.from : linkDates[0]
  const periodEnd = backendPeriod
    ? backendPeriod.to
    : linkDates[linkDates.length - 1]

  const period = periodStart
    ? `Period: ${format(periodStart)}${
        periodEnd && format(periodEnd) !== format(periodStart)
          ? ` - ${format(periodEnd)}`
          : ''
      }`
    : null

  return createPortal(
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.titleRow}>
              {data.isStrength !== undefined &&
                (data.isStrength ? (
                  <StrengthIcon className={styles.headerIcon} />
                ) : (
                  <ChallengeIcon className={styles.headerIcon} />
                ))}
              {/* Title + period share a column so the period aligns under the
                  title text rather than under the icon. */}
              <div className={styles.titleBlock}>
                <h2 className={styles.title}>{data.title}</h2>
                {period && <p className={styles.period}>{period}</p>}
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              type="button"
            >
              <span className={styles.closeIcon} />
            </button>
          </div>
        </div>

        <div
          className={classNames(styles.modalBody, {
            [styles.modalBodyScrollable]: isScrollable,
          })}
          ref={bodyRef}
        >
          {/* Structured breakdown (AI Listening) — falls back to the plain
              `details` paragraph for every module that doesn't produce one. */}
          {hasBreakdown ? (
            <>
              {data.observation && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>What we observed</h3>
                  <RichText text={data.observation} />
                </div>
              )}
              {data.whyItMatters && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Why it matters</h3>
                  <RichText text={data.whyItMatters} />
                </div>
              )}
              {data.likelyDriver && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Likely driver</h3>
                  <RichText text={data.likelyDriver} />
                </div>
              )}
            </>
          ) : (
            data.details && (
              <div className={styles.section}>
                <p className={styles.detailsText}>{data.details}</p>
              </div>
            )
          )}

          {/* AI Recommendation */}
          {data.aiRecommendation && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>AI Recommendation</h3>
              <p
                className={classNames(styles.recommendationText, {
                  [styles.recommendationChallenge]: data.isStrength === false,
                  [styles.recommendationStrength]: data.isStrength === true,
                })}
              >
                {data.aiRecommendation}
              </p>
            </div>
          )}

          {/* Suggested execution (AI Listening only) */}
          {data.suggestedExecution && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Suggested execution</h3>
              <RichText text={data.suggestedExecution} />
            </div>
          )}

          {/* Metrics row — per-module cards (Mention/Post/Chat/Review Links +
              the totals card). */}
          {data.metrics && data.metrics.length > 0 && (
            <div className={styles.metricsRow}>
              {data.metrics.map((card) => (
                <div className={styles.metricCard} key={card.title}>
                  <div className={styles.metricCardMain}>
                    <h3 className={styles.metricCardTitle}>{card.title}</h3>
                    <div className={styles.metricStats}>
                      {card.stats.map((stat) => (
                        <div
                          className={styles.metricStat}
                          key={stat.label ?? stat.emphasis}
                        >
                          <MetricStatIcon icon={stat.icon} />
                          {stat.label ? (
                            <span className={styles.mentionCountText}>
                              <span className={styles.mentionNumber}>
                                {stat.emphasis}
                              </span>{' '}
                              {stat.label}
                            </span>
                          ) : (
                            <span
                              className={classNames(styles.metricValue, {
                                [styles.metricValueDark]: stat.tone === 'dark',
                              })}
                            >
                              {stat.emphasis}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Donut sits to the right of a full-height divider. */}
                  {card.chartPercent != null && (
                    <>
                      <span className={styles.metricDivider} />
                      <MetricDonut percent={card.chartPercent} />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Data / Citation Sources — hidden for aggregate (period-level) cards */}
          {data.sources.length > 0 && !data.isAggregate && (
            <div className={styles.sourcesSection}>
              <h3 className={styles.sectionTitle}>
                {data.sources.some(isAiToolSource)
                  ? 'Citation Sources'
                  : 'Data Sources'}
              </h3>

              {/* Platform Filters */}
              <div className={styles.platformFilters}>
                <button
                  className={
                    selectedPlatform === 'all'
                      ? styles.filterButton
                      : styles.filterButtonOutline
                  }
                  onClick={() => setSelectedPlatform('all')}
                  type="button"
                >
                  All Sources
                </button>
                {platforms.map((platform) => (
                  <button
                    className={
                      selectedPlatform === platform.toLowerCase()
                        ? styles.filterButton
                        : styles.filterButtonOutline
                    }
                    key={platform}
                    // Clicking the active platform again toggles back to "All
                    // Sources".
                    onClick={() =>
                      setSelectedPlatform((prev) =>
                        prev === platform.toLowerCase()
                          ? 'all'
                          : platform.toLowerCase()
                      )
                    }
                    type="button"
                  >
                    {platformDisplayLabel(platform)}
                  </button>
                ))}
              </div>

              {/* Source Items */}
              <div className={styles.sourceItems}>
                {filteredSources
                  .filter((s) => s.links && s.links.length > 0)
                  .map((source, sourceIndex) => (
                    <div className={styles.sourceItem} key={sourceIndex}>
                      <div className={styles.linksList}>
                        {source.links!.map((link, linkIndex) => (
                          <div className={styles.linkItem} key={linkIndex}>
                            <div className={styles.sourceHeader}>
                              <div className={styles.sourcePlatform}>
                                <div className={styles.platformInfo}>
                                  <div className={styles.platformNameRow}>
                                    <span className={styles.platformName}>
                                      {(() => {
                                        const resolved =
                                          detectPlatformFromUrl(link.url) ||
                                          source.platform ||
                                          ''
                                        return platformDisplayLabel(
                                          resolved,
                                          formatPlatformName(resolved)
                                        )
                                      })()}
                                    </span>
                                    {(link.customerName || link.author) && (
                                      <span
                                        className={styles.sourceCustomer}
                                        title={link.customerName || link.author}
                                      >
                                        · {link.customerName || link.author}
                                      </span>
                                    )}
                                    {link.url && (
                                      <a
                                        className={styles.externalLink}
                                        href={
                                          resolveExternalUrl(link.url) || '#'
                                        }
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        <svg
                                          className={styles.externalLinkIcon}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                          />
                                        </svg>
                                      </a>
                                    )}
                                  </div>
                                  {link.date && (
                                    <span className={styles.sourceDate}>
                                      {formatDate(link.date)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {link.language && (
                                <span className={styles.sourceLanguage}>
                                  <PlanetIcon className={styles.languageIcon} />
                                  {formatLanguage(link.language)}
                                </span>
                              )}
                            </div>
                            {link.text && (
                              <p className={styles.linkText}>{link.text}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                {/* Flat sources (AI Listening): a verbatim quote per AI tool
                    with the cited URL, rather than a list of mention links. */}
                {filteredSources
                  .filter((s) => !s.links?.length && s.text)
                  .map((source, sourceIndex) => (
                    <div
                      className={styles.sourceItem}
                      key={`flat-${sourceIndex}`}
                    >
                      <div className={styles.linkItem}>
                        <div className={styles.citationRow}>
                          <span className={styles.citationTool}>
                            {(() => {
                              const ToolIcon = getAiToolIcon(
                                (source.platform || '').trim()
                              )
                              return ToolIcon ? (
                                <ToolIcon className={styles.citationToolIcon} />
                              ) : null
                            })()}
                            <span className={styles.citationToolName}>
                              {formatPlatformName(source.platform)}
                            </span>
                          </span>
                          {/* Some answers cite nothing, so this half is
                              conditional. */}
                          {source.url && (
                            <span className={styles.citedFrom}>
                              <span className={styles.citedFromLabel}>
                                Cited from:
                              </span>
                              <a
                                className={styles.citedFromLink}
                                href={resolveExternalUrl(source.url) || '#'}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                <PlanetIcon className={styles.citedFromGlobe} />
                                {citedDomain(source.url)}
                              </a>
                            </span>
                          )}
                        </div>
                        {data.sourceDate && (
                          <span className={styles.citationDate}>
                            {formatDate(data.sourceDate)}
                          </span>
                        )}
                        <p className={styles.linkText}>{source.text}</p>
                      </div>
                    </div>
                  ))}
                <div style={{ flexShrink: 0, height: '14px' }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
